import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { SeatMapWidget } from '@silverrailtech/srtlibrary';
import { styled } from '@mui/system';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

import { getSeatMap } from '../../../../../../api';
import { gaEvent } from '../../../../../../utils/googleAnalytics';
import messages from '../../messagesReservations';
import '../../reservations.css';
import ExtendedSnackbar from '../../../../../../components/ExtendedSnackbar/ExtendedSnackbar';
import messagesSeatMap from './messagesSeatMap';
import inlineStyles from './styles';
import { Wrapper, SeatMapWrapper } from './styled';

const StyledDialog = styled(Dialog)(() => ({
	'& .MuiPaper-root': {
		...inlineStyles.dialogPaper,
	},
}));

export const fetchSeatMapData = async (
	languageCode,
	travelSegments,
	setSeatMap,
	setErrorText,
	setIsProcessing,
	shoppingContext,
) => {
	travelSegments.forEach(async (segment) => {
		if (segment.SeatMapInformation) {
			return getSeatMap(
				{
					externalproposal: segment.SeatMapInformation.ExternalProposal,
					externaltravelsegment:
						segment.SeatMapInformation.ExternalTravelSegment,
					externalSupplier: segment.SeatMapInformation.ExternalSupplier,
					externalSearchToken: segment.SeatMapInformation.ExternalSearchToken,
					carrier: segment.SeatMapInformation.Carrier,
					vehicleId: segment.SeatMapInformation.VehicleId,
					boardLocation: segment.SeatMapInformation.BoardLocation,
					alightLocation: segment.SeatMapInformation.AlightLocation,
					departureDateTime: segment.SeatMapInformation.DepartureDateTime,
					ticketClass: segment.SeatMapInformation.TicketClass,
					inventoryCode: segment.SeatMapInformation.InventoryCode, // new field
					context: shoppingContext,
				},
				(e) => {
					const errorMessage = e?.errorResponse?.message || e;
					setErrorText(errorMessage);
					setIsProcessing(false);
				},
				(data) => {
					const dataParsed = {
						...data,
						vehicle: {
							...data.vehicle,
							coaches: data.vehicle.coaches.map((coach) => ({
								...coach,
								decks: coach.decks.map((deck, idx) => ({
									...deck,
									id: `${coach.id}-${idx}`,
								})),
							})),
						},
					};

					setSeatMap((prev) => ({
						...prev,
						[segment.TravelSegmentID]: dataParsed,
					}));
					setIsProcessing(false);
				},
			);
		}
		return null;
	});
};

const RESIZE_WIDTH = 1600;

const SeatSelectorDialog = ({
	open,
	intl: { formatMessage, locale },
	onDiscardSeatSelector,
	onRequestSeats,
	seatMapData: seatMapDataFromProps,
	travelSegmentID,
	exchangeOrderMode,
	departStation,
	arriveStation,
	paxLength,
	shoppingContext,
	validatedSeatMapData,
}) => {
	const [seatMap, setSeatMap] = useState({});
	const [errorText, setErrorText] = useState('');
	const [selectedSeats, setSelectedSeats] = useState([]);
	const [isProcessing, setIsProcessing] = useState(true);
	const localeFiltered = locale.split('-');

	const seatMapData = validatedSeatMapData?.length
		? validatedSeatMapData
		: seatMapDataFromProps;

	const [isTabletOrMobile, setIsTabletOrMobile] = useState(
		window.innerWidth <= RESIZE_WIDTH,
	);

	useEffect(() => {
		const handleResize = () => {
			setIsTabletOrMobile(window.innerWidth <= RESIZE_WIDTH);
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (seatMapData) {
			fetchSeatMapData(
				localeFiltered[0],
				seatMapData,
				setSeatMap,
				setErrorText,
				setIsProcessing,
				shoppingContext,
			);
		}
	}, [seatMapData]);

	const seats = seatMap[travelSegmentID];

	const handleSeatChange = (e) => {
		const { value } = e.target;
		if (value) {
			setSelectedSeats(value);
		}
	};

	const handleDiscard = () => {
		if (exchangeOrderMode) {
			gaEvent('exchangeOrderSeatMapDiscardSeats');
		} else {
			gaEvent('seatmapComponentDiscard');
		}
		onDiscardSeatSelector();
	};

	const handleRequestSeats = () => {
		if (exchangeOrderMode) {
			gaEvent('exchangeOrderSeatMapRequestSeats');
		} else {
			gaEvent('seatmapComponentRequestSeats');
		}

		try {
			if (selectedSeats?.length) {
				onRequestSeats(travelSegmentID, selectedSeats);
			} else {
				// eslint-disable-next-line no-console
				console.log('No seats chosen');
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error in handleRequestSeats:', error);
		}
	};

	const handleSnackbarClose = () => {
		setErrorText('');
	};

	const actions = [
		<Button
			variant="contained"
			id="srtReservationsDiscard"
			key="srtReservationsDiscard"
			onClick={handleDiscard}
			style={inlineStyles.buttons}
		>
			{formatMessage(messages.btnDiscard)}
		</Button>,
		<Button
			variant="contained"
			id="srtReservationsRequest"
			key="srtReservationsRequest"
			onClick={handleRequestSeats}
			color="primary"
			style={inlineStyles.buttons}
		>
			{formatMessage(messages.btnReqestSeats)}
		</Button>,
	];

	return (
		<>
			<StyledDialog
				open={open}
				onClose={handleDiscard}
				disableEnforceFocus
				maxWidth={false}
			>
				<DialogTitle>
					<FormattedMessage
						{...messagesSeatMap.lblSeatMapTitle}
						values={{ departStation, arriveStation }}
					/>
				</DialogTitle>
				<DialogContent>
					<Wrapper>
						{!isProcessing && seats && seats?.vehicle && (
							<SeatMapWrapper>
								<SeatMapWidget
									selectedSeats={selectedSeats?.length ? selectedSeats : null}
									key={`${seats.vehicle.id}`}
									vehicle={seats?.vehicle}
									maxSeats={paxLength}
									variant={isTabletOrMobile ? 'Vertical' : 'Horizontal'}
									coachTitle={
										<FormattedMessage
											{...messagesSeatMap.lblSelectPreferredCoach}
										/>
									}
									seatMapTitle={
										<FormattedMessage
											{...messagesSeatMap.lblSelectPreferredSeat}
										/>
									}
									seatMapLabels={{
										selected: {
											label: (
												<FormattedMessage {...messagesSeatMap.lblSelected} />
											),
										},
										unavailable: {
											label: (
												<FormattedMessage {...messagesSeatMap.lblUnavailable} />
											),
										},
										available: {
											label: (
												<FormattedMessage {...messagesSeatMap.lblAvailable} />
											),
										},
										direction: {
											label: (
												<FormattedMessage
													{...messagesSeatMap.lblDirectionOfTravel}
												/>
											),
										},
									}}
									onChange={handleSeatChange}
								/>
							</SeatMapWrapper>
						)}
					</Wrapper>
				</DialogContent>
				<DialogActions>{actions}</DialogActions>
			</StyledDialog>
			<ExtendedSnackbar
				id="srtSeatMapSnackBar"
				open={errorText !== ''}
				message={errorText}
				onClose={handleSnackbarClose}
			/>
		</>
	);
};

const mapStateToProps = (state) => {
	const seatMapData = state.getIn(['booking', 'seatMap']);
	return {
		seatMapData: seatMapData ? seatMapData.toJS() : seatMapData,
	};
};

SeatSelectorDialog.propTypes = {
	intl: PropTypes.object,
	open: PropTypes.bool.isRequired,
	onDiscardSeatSelector: PropTypes.func.isRequired,
	onRequestSeats: PropTypes.func.isRequired,
	seatMapData: PropTypes.array,
	travelSegmentID: PropTypes.string,
	exchangeOrderMode: PropTypes.bool,
	departStation: PropTypes.string,
	arriveStation: PropTypes.string,
	paxLength: PropTypes.number,
	shoppingContext: PropTypes.string,
	validatedSeatMapData: PropTypes.array,
};

export { SeatSelectorDialog as SeatSelectorDialogAlias };

export default connect(mapStateToProps, null)(injectIntl(SeatSelectorDialog));
